import React, { useMemo, useState } from 'react'

import { useSelector } from 'react-redux'

import { useSnackbar } from '../../../../../../../../../../shared/components/Snackbar/context/SnackBar.context.container'

import Button from '../../../../../../../../../../shared/components/Button/Button.component'
import Modal from '../../../../../../../../../../shared/components/Modal/Modal.component'

import { cancelOfferRequestForAdmin } from '../../../../../../../../../../redux/states/admin/orders/cancelOfferRequestForAdmin/cancelOfferRequestForAdmin.slice'
import { RootState } from '../../../../../../../../../../redux/store/store.types'
import status from '../../../../../../../../../../shared/functions/Status/status.dictionaries'
import { useAppDispatch } from '../../../../../../../../../../shared/hooks/redux.hooks'
import styles from './CancelOfferRequestButton.styles'
import { CancelOfferRequestButtonProps } from './CancelOfferRequestButton.types'

const CancelOfferRequestButton: React.FC<CancelOfferRequestButtonProps> = (props) => {
  const { offerRequestId, itemRequests, onSuccess } = props

  const [isModalOpen, setIsModalOpen] = useState(false)
  const dispatch = useAppDispatch()
  const { show } = useSnackbar()

  const { loading: cancelLoading, success: cancelOfferRequestSuccess } = useSelector(
    (state: RootState) => state.cancelOfferRequestForAdmin
  )
  const { success: offerRequestsSuccess } = useSelector((state: RootState) => state.getAllOfferRequestsForAdmin)

  const offerRequest = offerRequestsSuccess?.data?.records.find((offerRequest) => offerRequest.id === offerRequestId)

  // Check if any item requests within this offer request have been accepted by travelers
  const hasAcceptedItemRequests = useMemo(() => {
    if (!itemRequests) return false

    return itemRequests.some((itemRequest) => itemRequest.status?.name === status.itemRequestStatus.acceptedByTraveler)
  }, [itemRequests])

  const isOfferRequestCancelled = useMemo(() => {
    // If we have a successful cancellation for this specific offer request, use that status
    if (cancelOfferRequestSuccess?.data?.offerRequest?.id === String(offerRequestId)) {
      return cancelOfferRequestSuccess.data.offerRequest.status?.name === status.offerRequestStatus.cancelled_by_admin
    }

    // Otherwise, fall back to the original offer request status
    return (
      offerRequest?.status?.name === status.itemRequestStatus.cancelledByShopper ||
      offerRequest?.status?.name === status.offerRequestStatus.cancelled_by_admin
    )
  }, [offerRequest?.status?.name, cancelOfferRequestSuccess?.data, offerRequestId])

  const getButtonText = () => {
    if (isOfferRequestCancelled) {
      return 'Offer Cancelled'
    }

    return 'Cancel Offer'
  }

  const getTooltipText = () => {
    if (hasAcceptedItemRequests) {
      return 'Cannot cancel offer request when item requests have been accepted by travelers'
    }

    if (isOfferRequestCancelled) {
      return 'Offer cancelled'
    }

    return undefined
  }

  const isButtonDisabled = hasAcceptedItemRequests || isOfferRequestCancelled

  const handleCancelClick = () => {
    if (!isButtonDisabled) {
      setIsModalOpen(true)
    }
  }

  const handleConfirmCancel = () => {
    dispatch(
      cancelOfferRequestForAdmin({
        request: { offerRequestId },
        onSuccess: () => {
          setIsModalOpen(false)
          show({ message: 'Offer request cancelled successfully.', severity: 'success' })
          onSuccess && onSuccess()
        }
      })
    )
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  return (
    <>
      <Button
        text={getButtonText()}
        buttonType="error"
        disabled={isButtonDisabled}
        onClick={handleCancelClick}
        tooltipText={getTooltipText()}
        style={styles.button}
      />

      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        title="Cancel Offer Request"
        primaryButton={{
          label: 'Confirm',
          onClick: handleConfirmCancel,
          loading: cancelLoading
        }}
        secondaryButton={{
          label: 'Cancel',
          onClick: handleCloseModal
        }}
        body="Are you sure you want to cancel this offer request?"
      />
    </>
  )
}

export default CancelOfferRequestButton
